﻿import type { DeviceDetail, DeviceStatusLogItem } from "@/types.ts";
import { AdminButton, AdminSelect, AdminTextarea } from "@/components/admin";
import type { AdminDeviceConnectionEvent } from "@/types.ts";
import { formatDateTime } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
interface DeviceDetailPanelProps {
    device: DeviceDetail | null;
    logs: DeviceStatusLogItem[];
    connectionEvents: AdminDeviceConnectionEvent[];
    connectionEventsError: string | null;
    loading: boolean;
    saving: boolean;
    auditMessage: string | null;
    onClose: () => void;
    onRefresh: () => void;
    onStatusChange: (status: string, reasonCode: string, reason: string) => void;
}
const statusBadge: Record<string, string> = {
    ACTIVE: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    INACTIVE: "bg-orange-50 text-orange-700 border border-orange-200",
    REVOKED: "bg-red-50 text-red-700 border border-red-200",
};
const statusOptions = [
    { value: "ACTIVE", label: "활성" },
    { value: "INACTIVE", label: "비활성" },
    { value: "REVOKED", label: "해제" },
];
const labelOf = (device: DeviceDetail) => {
    return device.statusShortName ?? device.statusName ?? device.status;
};
const DetailRow = ({ label, value }: {
    label: string;
    value: string | number;
}) => {
    return (<div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-slate-700 font-medium text-right break-all">{value}</span>
    </div>);
};
const DeviceDetailPanel = ({ device, logs, connectionEvents, connectionEventsError, loading, saving, auditMessage, onClose, onRefresh, onStatusChange, }: DeviceDetailPanelProps) => {
    const navigate = useNavigate();
    const [nextStatus, setNextStatus] = useState("");
    const [reasonCode, setReasonCode] = useState("OPERATOR_REQUEST");
    const [reason, setReason] = useState("");
    useEffect(() => {
        setNextStatus(device?.status ?? "");
        setReason("");
    }, [device?.deviceId, device?.status]);
    if (!device) {
        return (<div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full bg-slate-100">
            <i className="ri-smartphone-line text-slate-400 text-lg"/>
          </div>
          <h4 className="text-sm font-semibold text-slate-600 mb-1">기기를 선택하세요</h4>
          <p className="text-xs text-slate-400">목록에서 상세 버튼을 누르면 기기 정보가 표시됩니다.</p>
        </div>
      </div>);
    }
    const latestLog = logs[0] ?? null;
    return (<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-slate-800">기기 상세</h3>
          <AdminButton type="button" variant="ghost" size="icon" icon="ri-close-line" onClick={onClose} aria-label="기기 상세 닫기"/>
        </div>
        <p className="text-xs text-slate-400">{device.deviceName ?? `기기 #${device.deviceId}`} · {device.deviceUuid}</p>
      </div>

      <div className="px-4 py-3 space-y-4 max-h-[calc(100vh-220px)] overflow-y-auto">
        {loading && <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2">기기 상세 정보를 불러오는 중입니다.</p>}

        <section>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">기본 정보</h4>
          <div className="space-y-1.5">
            <DetailRow label="기기 ID" value={device.deviceId}/>
            <DetailRow label="기기명" value={device.deviceName ?? "-"}/>
            <DetailRow label="기기 UUID" value={device.deviceUuid}/>
            <DetailRow label="플랫폼" value={device.platformType}/>
            <DetailRow label="앱 버전" value={device.appVersion ?? "-"}/>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">상태</span>
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${statusBadge[device.status] ?? "bg-slate-50 text-slate-600 border border-slate-200"}`}>
                {labelOf(device)}
              </span>
            </div>
            <DetailRow label="최근 온라인" value={formatDateTime(device.lastOnlineAt)}/>
            <DetailRow label="등록 시각" value={formatDateTime(device.createdAt)}/>
          </div>
        </section>

        <section>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">연결 어르신</h4>
          <div className="space-y-1.5">
            <DetailRow label="이름" value={device.elderlyName}/>
            <DetailRow label="Senior 계정 ID" value={device.seniorUserAccountId}/>
            {device.seniorUserAccountId > 0 && (<div className="mt-3 grid grid-cols-1 gap-2">
                <AdminButton type="button" variant="secondary" size="sm" icon="ri-user-heart-line" onClick={() => navigate(`/admin/users/seniors/${device.seniorUserAccountId}`)}>
                  Senior 통합 상세 보기
                </AdminButton>
                <AdminButton type="button" variant="soft" size="sm" icon="ri-heart-pulse-line" onClick={() => navigate(`/admin/users/seniors/${device.seniorUserAccountId}?tab=care`)}>
                  생활신호 보기
                </AdminButton>
              </div>)}
          </div>
        </section>

        <section>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">최근 상태 로그</h4>
          {latestLog ? (<div className="space-y-1.5">
              <DetailRow label="배터리" value={latestLog.batteryLevel == null ? "-" : `${latestLog.batteryLevel}%`}/>
              <DetailRow label="네트워크" value={latestLog.networkStatus}/>
              <DetailRow label="앱 상태" value={latestLog.appStatus}/>
              <DetailRow label="권한 상태" value={latestLog.permissionStatus ?? "-"}/>
              <DetailRow label="기록 시각" value={formatDateTime(latestLog.recordedAt)}/>
            </div>) : (<p className="text-xs text-slate-400">최근 상태 로그가 없습니다.</p>)}
        </section>

        <section>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">최근 연결 원본 이력</h4>
          {connectionEventsError ? (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{connectionEventsError}</p>
          ) : connectionEvents.length > 0 ? (
            <div className="space-y-2">
              {connectionEvents.slice(0, 5).map((event) => (
                <div key={event.connectionEventId} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <strong className="text-slate-700">{event.eventType}</strong>
                    <span className="text-slate-500">{event.previousState} → {event.currentState}</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-400">발생 {formatDateTime(event.occurredAt)} · 감지 {formatDateTime(event.detectedAt)}</p>
                  {event.reason && <p className="mt-1 text-xs text-slate-500">{event.reason}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400">이 기기의 최근 연결 이력이 없습니다.</p>
          )}
        </section>
      </div>

      <div className="px-4 py-3 border-t border-slate-100 space-y-2">
        <AdminSelect value={reasonCode} onValueChange={setReasonCode} disabled={saving}>
          <option value="OPERATOR_REQUEST">운영자 요청</option><option value="DATA_CORRECTION">데이터 정정</option><option value="SECURITY_RESPONSE">보안 대응</option><option value="TEST_ROLLBACK">테스트 원복</option><option value="OTHER">기타</option>
        </AdminSelect>
        <AdminTextarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} rows={3} placeholder="변경 사유를 10자 이상 입력하세요." disabled={saving} />
        {auditMessage && <p className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">{auditMessage}</p>}
        <div className="flex items-center gap-2">
        <AdminButton type="button" variant="primary" size="sm" icon={`ri-refresh-line ${loading ? "animate-spin" : ""}`} onClick={onRefresh} disabled={loading} className="flex-1">
          새로고침
        </AdminButton>
        <AdminSelect value={nextStatus} onValueChange={setNextStatus} disabled={saving} className="w-auto min-w-20">
          {statusOptions.map((option) => (<option key={option.value} value={option.value}>
              {option.label}
            </option>))}
        </AdminSelect>
        <AdminButton type="button" size="sm" disabled={saving || nextStatus === device.status || reason.trim().length < 10} onClick={() => onStatusChange(nextStatus, reasonCode, reason)}>상태 변경</AdminButton>
        </div>
      </div>
    </div>);
};
export default DeviceDetailPanel;
